import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ApplicationInfo = () => {
  const navigate = useNavigate();
  const [deadline, setDeadline] = useState('');
  const [startDate, setStartDate] = useState('');

  useEffect(() => {
    fetch('/api/settings/deadline')
      .then(res => res.json())
      .then(data => { setStartDate(data.startDate || ''); setDeadline(data.deadline || ''); })
      .catch(() => {});
  }, []);

  const now = new Date();
  const appOpen = startDate ? new Date(startDate) : null;
  const appClose = deadline ? new Date(deadline) : null;
  const isBeforeOpen = appOpen && now < appOpen;
  const isAfterDeadline = appClose && now > appClose;
  const canApply = !isBeforeOpen && !isAfterDeadline;

  const timeline = [
    {
      step: 1,
      title: "Submit Application",
      description: "Complete the student application form with all required information and upload required documents",
      duration: "Online"
    },
    {
      step: 2,
      title: "Confirmation Email",
      description: "Receive a confirmation email acknowledging receipt of your application",
      duration: "Within 24 hours"
    },
    {
      step: 3,
      title: "Initial Review & Document Screening",
      description: "Our admissions team reviews your application and verifies all submitted documents",
      duration: "1 week after submission"
    },
    {
      step: 4,
      title: "Placement Exam",
      description: "Only applicants with accepted applications after document screening will be invited to take the placement exam at the school premises",
      duration: "29th July 2026"
    },
    {
      step: 5,
      title: "Admission Decision",
      description: "Receive your final admission decision via email after NESA exam results are released",
      duration: "1-2 weeks after NESA results"
    }
  ];

  const benefits = [
    "Holistic education approach",
    "REB-approved curriculum",
    "Modern learning facilities",
    "Extracurricular activities",
    "Safe and supportive environment"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8 md:py-16 px-4 sm:px-6 relative">
        <img src="/school.png" alt="Hope Haven Christian School" className="h-16 sm:h-20 md:h-24 lg:h-32 mx-auto md:absolute md:top-4 md:left-4 md:mx-0 mb-2 md:mb-0" />
        <div className="max-w-6xl mx-auto text-center md:pt-0">
          <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 md:mb-2">Hope Haven Christian School</h1>
          <p className="text-sm sm:text-lg md:text-xl text-blue-100">Admission Application Portal</p>
        </div>
      </div>

      {/* Deadline Banner */}
      {deadline && (
        <div className="bg-yellow-50 border-l-4 border-yellow-600 p-3 sm:p-4 max-w-6xl mx-3 sm:mx-auto mt-4 sm:mt-6 rounded shadow">
          <p className="text-yellow-800 font-semibold text-center text-sm sm:text-base">
            {isBeforeOpen ? (
              <>Applications open <span className="text-base sm:text-lg">{appOpen.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span></>
            ) : isAfterDeadline ? (
              <span className="text-red-700">Application period has closed. No new submissions are accepted.</span>
            ) : (
              <>Application Deadline: <span className="text-base sm:text-lg">{appClose.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span></>
            )}
          </p>
        </div>
      )}

      {/* Requirements Section (top) */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <section className="mb-6 sm:mb-8">
          <div className="bg-blue-50 border-l-4 border-blue-600 p-3 sm:p-6 rounded shadow">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Application Requirements</h3>
            <div className="bg-yellow-50 border border-yellow-400 rounded p-3 mb-4 text-sm sm:text-base">
              <p className="font-semibold text-yellow-800 mb-1">Please read carefully before applying</p>
              <p className="text-yellow-700">Make sure you have all required documents ready and a valid email address. Failure to follow the requirements may result in your application being rejected.</p>
            </div>
            <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
              <li>• <strong>Valid Email Required:</strong> All communication regarding your application will be sent via email. Make sure to provide a valid and accessible email address and check it regularly for updates.</li>
              <li>• <strong>One Application Per Email:</strong> Each email address can submit only one application</li>
              <li>• <strong>Verification Required:</strong> You must verify your email before proceeding</li>
              <li>• <strong>Complete Information:</strong> Ensure all required fields are filled accurately</li>
              <li>• <strong>Non-Refundable Application Fee:</strong> A non-refundable application fee of <strong>100,000 Rwf</strong> is required with each submission. Pay to Account <strong>#100145699155 — HOPE HAVEN LTD</strong> at BANK OF KIGALI</li>
              <li>• <strong>Required Documents:</strong> Upload the previous academic year report card and proof of payment (100,000 Rwf) in PDF, JPG, or PNG format. <strong>Please prepare documents under 1MB</strong> to ensure successful upload and avoid system rejection.</li>
              <li>• <strong>Document Guidelines:</strong> Carefully review what documents are required for your specific class. Senior 1 and Senior 4 applicants need multiple year report cards as specified below.</li>
              <li>• <strong>Academic Requirement:</strong> Applicants must have scored <strong>80% or above</strong> in the previous academic year. Applications with marks below 80% will not be accepted</li>
              <li>• <strong>Senior 1 Applicants (National):</strong> Must upload Primary 5 and Primary 6 Term 1 & 2 report cards with an average above 80%</li>
              <li>• <strong>Senior 1 Applicants (International/Cambridge):</strong> Must upload Grade 6 or Year 7 report cards with an average above 80%</li>
              <li>• <strong>Senior 4 Applicants (National):</strong> A very limited number of spots are available. Must upload S1, S2, and S3 grade reports with an average above 80%</li>
              <li>• <strong>Senior 4 Applicants (International/Cambridge):</strong> Must upload Grade 10 or Year 11 report cards with an average above 80%</li>
              <li>• <strong>Parent Commitment:</strong> Parents must commit to paying 100% of the school fees (per term) if the student excels in both entry and national exams</li>
              <li>• <strong>School Fees:</strong> Fee amounts (per term) are displayed after selecting the class in the application form</li>
              <li>• <strong>Application Deadline:</strong> {deadline ? new Date(deadline).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Check back for updates'} — applications received after this date will not be accepted</li>
              <li>• <strong>Contact Support:</strong> Reach out to enrollment@hopehavenschool.org for any question</li>
              <li>• <strong>School Address:</strong> 26P4+6W, Kigali, Rwanda</li>
            </ul>
          </div>
        </section>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4">
        
        {/* About Section */}
        <section className="mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 sm:mb-8 text-center">About Our School</h2>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
            <div className="bg-white p-4 sm:p-8 rounded-lg shadow-md">
              <h3 className="text-lg sm:text-xl font-bold text-blue-600 mb-3 sm:mb-4">Our Mission</h3>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                Hope Haven Rwanda transforms students and their families through Christ-centered 
                education and discipleship with uncompromising excellence, shaping the future of 
                Rwanda and stewarding Christian world leaders.
              </p>
            </div>
            <div className="bg-white p-4 sm:p-8 rounded-lg shadow-md">
              <h3 className="text-lg sm:text-xl font-bold text-blue-600 mb-3 sm:mb-4">Our Vision</h3>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                Hope Haven Rwanda forges a future where our Christian community leads the flourishing of Rwanda and influences the world.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 sm:mb-8 text-center">Why Choose Us</h2>
          <div className="bg-white p-4 sm:p-8 rounded-lg shadow-md">
            <ul className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-md bg-blue-600 text-white text-xs sm:text-base">
                      ✓
                    </div>
                  </div>
                  <p className="ml-3 sm:ml-4 text-gray-700 text-sm sm:text-lg">{benefit}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Application Timeline */}
        <section className="mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-8 sm:mb-12 text-center">Application Process & Timeline</h2>
          
          <div className="space-y-6 sm:space-y-8">
            {timeline.map((item, index) => (
              <div key={index} className="flex gap-3 sm:gap-4">
                {/* Timeline dot and line */}
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-white text-sm sm:text-base mb-2 ${
                    index % 2 === 0 ? 'bg-blue-600' : 'bg-blue-500'
                  }`}>
                    {item.step}
                  </div>
                  {index !== timeline.length - 1 && (
                    <div className="w-1 h-16 sm:h-20 bg-gradient-to-b from-blue-400 to-blue-200"></div>
                  )}
                </div>

                {/* Content */}
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md flex-1 mt-1">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                    <div>
                      <h3 className="text-base sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm sm:text-base">{item.description}</p>
                    </div>
                    <span className="bg-blue-100 text-blue-800 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap self-start">
                      {item.duration}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Button */}
        <section className="text-center py-8 sm:py-12">
          <p className="text-base sm:text-xl text-gray-700 mb-6 sm:mb-8">
            {isBeforeOpen ? (
              <>Applications will open on <strong>{appOpen.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</strong>. Please check back then.</>
            ) : isAfterDeadline ? (
              <>The application period has closed. Thank you for your interest.</>
            ) : (
              'Ready to start your journey with us?'
            )}
          </p>
          {canApply && (
            <button
              onClick={() => navigate('/apply')}
              className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-bold py-3 sm:py-4 px-8 sm:px-12 rounded-lg text-base sm:text-lg shadow-lg transform hover:scale-105 transition duration-200"
            >
              Start Your Application Now
            </button>
          )}
        </section>
      </div>
    </div>
  );
};

export default ApplicationInfo;
